import React, {useEffect, useRef, useState} from 'react';
import {css, styled} from 'styled-components';
import {colors} from '../../styles/colors';
import DefaultImageView from './DefaultImageView';
import DefaultButton from './DefaultButton';
import DefaultText, {TextStyle} from './DefaultText';
import {useLocation, useNavigate} from 'react-router-dom';
import {useRecoilState} from 'recoil';
import {baseUrlTypeState, userInfoState} from 'utils/Atoms';
import {useMediaQuery} from 'react-responsive';
import {minmax} from 'utils/minmax';
import DefaultFormattedMessage from './DefaultFormattedMessage';
import CommonModal from './CommonModal';
import {logout} from './ScreenLayout';
import useInfiniteScrolling from 'utils/useInfiniteScrolling';
import {useOutSideRef} from 'utils/useOutSideRef';

/**
 * 헤더 컴포넌트
 * 상단 헤더 영역을 표시하는 컴포넌트입니다.
 * @returns {JSX.Element} 헤더 컴포넌트
 */
const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const isChangeStyle = useMediaQuery({
    query: '(max-width:1319px)',
  });

  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const [baseUrlType, setBaseUrlType] = useRecoilState(baseUrlTypeState);

  const noticeRef = useRef<HTMLDivElement>(null);

  const [isOpenNotice, setIsOpenNotice] = useState(false);

  const [modal, setModal] = useState('');

  const [searchListOpen, setSearchListOpen] = useState(false);
  const [siteData, setSiteData] = useState<any[] | null>(null);

  // 무한 스크롤
  const listRef = useRef(null);
  // 검색 ref
  const searchRef = useRef(null);
  useOutSideRef(searchRef, () => setSearchListOpen(false));
  const [observerRef, setObserverRef] = useState<null | HTMLDivElement>(null);
  // 스크롤 정보
  const [scrollData, setScrollData] = useState({
    page: 1,
    list: [],
    lastOfList: false,
    noResults: false,
  });

  const getInfo = async (isFetch?: boolean) => {
    // 현장 리스트 가져오기 함수
  };

  // 스크롤 update
  const fetchBoxList = React.useCallback(async () => {
    await getInfo(true);
  }, [scrollData.page]);

  // 무한 스크롤 함수
  useInfiniteScrolling({
    observerRef,
    fetchMore: fetchBoxList,
    hasMore: !scrollData.lastOfList && !scrollData.noResults,
  });

  // 알림
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        noticeRef?.current &&
        !noticeRef?.current?.contains(e.target as Node)
      ) {
        setIsOpenNotice(false);
      }
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [noticeRef]);

  // 현장 리스트
  useEffect(() => {
    getInfo();
  }, [pathname]);

  const modalData: {[key: string]: any} = {
    // 본사시스템 서비스 준비중
    servicePreparing: {
      title: 'servicePreparing',
      firstButtonText: 'confirm',
      firstButtonClick: () => {
        setModal('');
      },
    },
  };

  return (
    <Container
      style={{
        zIndex: modal || isOpenNotice || searchListOpen ? 100 : 0,
      }}>
      <SplitWrap>
        <LogoContainer $isChangeStyle={0}>
          <DefaultImageView
            source={`images/common/${
              // isChangeStyle ? 'header_logo_small' : 'header_logo'
              'header_logo'
            }.png`}
            style={{
              height: '100%',
              cursor: 'pointer',
            }}
            onClick={() => {
              // 메인
              navigate('/main');
            }}
          />
        </LogoContainer>
        {/* 본사 시스템 */}
        <DefaultImageView
          source="images/common/header_crown.png"
          style={{
            ...SplitWrapImgStyle,
            marginLeft: minmax(20),
          }}
        />
        <DefaultText text="headquarterSystem" textStyle={SplitWrapTextStyle} />

        {/* <Border style={{margin: `0 ${minmax(20)}`}} /> */}

        {/* 본사 공지 */}
        {/* <DefaultText
          text="headOfficeAnnouncement"
          textStyle={{...SplitWrapTextStyle, cursor: 'pointer'}}
          onClick={() => {
            navigate('/notice');
          }}
        /> */}

        {/* 사용량 */}
        {/* <Border style={{margin: `0 ${minmax(20)}`}} />

        <DefaultText
          text="사용량"
          textStyle={{...SplitWrapTextStyle, cursor: 'pointer'}}
          onClick={() => {
            navigate('/usage');
          }}
        /> */}
      </SplitWrap>

      <SplitWrap>
        {/* baseUrlType 표시 */}
        <DefaultText
          text={`${baseUrlType} (${
            process.env.REACT_APP_NODE_ENV === 'production'
              ? '프로덕션'
              : '개발'
          })`}
          textStyle={SplitWrapTextStyle}
          onClick={() => {
            navigate('/notice');
          }}
        />

        <Border style={{margin: `0 ${minmax(20)}`}} />

        {/* 이름 */}
        <DefaultFormattedMessage
          id="sirFormat"
          values={{
            name: 'ADMIN ',
            b: (format: string) => (
              <TextStyle
                style={{...SplitWrapTextStyle, fontWeight: 500}}
                as="span">
                {format}
              </TextStyle>
            ),
          }}
          style={SplitWrapTextStyle}
          tooltip="myPage"
        />
        {/* 로그아웃 */}
        <DefaultButton
          text="logout"
          buttonStyle={LogoutButtonStyle}
          textStyle={LogoutTextStyle}
          onClick={() => {
            logout({
              setUserInfo,
              navigate,
              setBaseUrlType,
            });
          }}
        />
      </SplitWrap>

      {modal && (
        <CommonModal
          title={modalData[modal].title}
          detail={modalData[modal].detail}
          firstButtonText={modalData[modal].firstButtonText}
          firstButtonStyle={modalData[modal].firstButtonStyle}
          secondButtonText={modalData[modal].secondButtonText}
          secondButtonStyle={modalData[modal].secondButtonStyle}
          firstButtonClick={modalData[modal].firstButtonClick}
          secondButtonClick={modalData[modal].secondButtonClick}
          closeModal={modalData[modal].firstButtonClick}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  position: fixed;
  width: 100%;
  height: ${minmax(88)};
  /* padding: 0 ${minmax(28)}; */
  padding-right: ${minmax(28)};
  display: flex;
  column-gap: 1%;
  border-bottom: solid 1px ${colors.lightGrey};
  align-items: center;
  justify-content: space-between;
  background-color: ${colors.white};
  z-index: 999;
`;

const LogoContainer = styled.div<{$isChangeStyle: number}>`
  height: ${minmax(36)};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  justify-content: center;
  margin-left: ${minmax(28)};
`;

const SplitWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 3px;
`;

const Border = styled.div`
  width: 1px;
  height: ${minmax(22)};
  background-color: ${colors.gray};
`;

const SearchWrap = styled.div<{$open: number}>`
  position: relative;
  height: 60%;
  padding: 0 ${minmax(12, 10)};
  display: flex;
  align-items: center;
  flex: auto;
  position: relative;
  height: ${minmax(52)};
  padding: ${minmax(8)} ${minmax(12)};
  column-gap: ${minmax(12)};
  background-color: ${colors.lighterGray};
  border-radius: 6px;
  input {
    width: 100%;
    background-color: transparent;
    border: none;
    outline: none;
    font-weight: 500;
  }

  ${(props: any) =>
    props.$open &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}
`;

const SearchResultList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: ${colors.lighterGray};
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  border-top: 1px solid #585858;
  z-index: 10;
  max-height: ${minmax(230)};
  overflow-y: auto;
  p:hover {
    background-color: #31577a30;
  }
`;

const LogoutContainer = styled.div`
  .logout_img {
    width: ${minmax(40, 30)};
  }
  cursor: pointer;
`;

const LogoutTextStyle = {
  fontSize: minmax(25),
  color: colors.white,
};

const LogoutButtonStyle = {
  width: 'fit-content',
  // height: 'fit-content',
  height: minmax(40),
  padding: `${minmax(5)} ${minmax(10)}`,
  borderRadius: '30px',
  backgroundColor: colors.gray,
  marginLeft: minmax(20),
};

const SplitWrapImgStyle = {
  width: minmax(29),
  height: minmax(29),
};

const SplitWrapTextStyle: React.CSSProperties = {
  fontSize: minmax(25),
};

export default Header;
